import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Radio, CalendarDays, Plus, Trash2, Check, Users } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { AnimatedNotes } from "@/components/AnimatedNotes";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";

function fmtWhen(iso) {
  try {
    return new Date(iso).toLocaleString(undefined, { weekday: "long", day: "numeric", month: "long", hour: "2-digit", minute: "2-digit" });
  } catch (e) {
    return iso;
  }
}

function RegisterForm({ sessionId, onDone }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!name.trim() || !email.trim()) { toast.error("Please enter your name and email."); return; }
    setBusy(true);
    try {
      await api.post(`/live-sessions/${sessionId}/register`, { name: name.trim(), email: email.trim() });
      toast.success("You're registered! Check your email for the details.");
      onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };
  return (
    <div className="mt-4 flex flex-col gap-2 sm:flex-row" data-testid="live-register-form">
      <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" data-testid="live-reg-name" className="flex-1 rounded-full border border-border bg-background px-4 py-2 text-sm outline-none focus:border-primary" />
      <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" type="email" data-testid="live-reg-email" className="flex-1 rounded-full border border-border bg-background px-4 py-2 text-sm outline-none focus:border-primary" />
      <Button onClick={submit} disabled={busy} data-testid="live-reg-submit" className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">{busy ? "…" : "Confirm"}</Button>
    </div>
  );
}

function AdminForm({ onCreated }) {
  const [f, setF] = useState({ title: "", description: "", scheduled_at: "", platform: "Zoom", join_url: "" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const submit = async () => {
    if (!f.title || !f.scheduled_at) { toast.error("Title and date/time are required."); return; }
    setBusy(true);
    try {
      await api.post("/live-sessions", { ...f, scheduled_at: new Date(f.scheduled_at).toISOString() });
      toast.success("Session scheduled.");
      setF({ title: "", description: "", scheduled_at: "", platform: "Zoom", join_url: "" });
      onCreated();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };
  return (
    <div className="mb-10 rounded-2xl border border-primary/30 bg-card p-6" data-testid="live-admin-form">
      <p className="flex items-center gap-2 font-serif text-xl font-semibold"><Plus size={18} className="text-primary" /> Schedule a live session</p>
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <input value={f.title} onChange={(e) => set("title", e.target.value)} placeholder="Title" data-testid="live-admin-title" className="rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary" />
        <input value={f.scheduled_at} onChange={(e) => set("scheduled_at", e.target.value)} type="datetime-local" data-testid="live-admin-datetime" className="rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary" />
        <input value={f.platform} onChange={(e) => set("platform", e.target.value)} placeholder="Platform (Zoom / YouTube)" className="rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary" />
        <input value={f.join_url} onChange={(e) => set("join_url", e.target.value)} placeholder="Join URL (optional)" className="rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary" />
        <textarea value={f.description} onChange={(e) => set("description", e.target.value)} placeholder="Description" rows={2} className="rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary sm:col-span-2" />
      </div>
      <Button onClick={submit} disabled={busy} data-testid="live-admin-submit" className="mt-4 rounded-full bg-primary text-primary-foreground hover:bg-primary/90">{busy ? "Saving…" : "Schedule session"}</Button>
    </div>
  );
}

export default function Live() {
  const { user } = useAuth();
  const isAdmin = user && user.role === "admin";
  const [data, setData] = useState({ upcoming: [], past: [] });
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/live-sessions").then(({ data }) => setData(data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const del = async (id) => {
    try { await api.delete(`/live-sessions/${id}`); toast.success("Deleted."); load(); } catch (e) { toast.error("Could not delete."); }
  };

  return (
    <div className="min-h-screen text-foreground">
      <Navbar />
      <section className="relative overflow-hidden border-b border-border">
        <AnimatedNotes count={10} />
        <div className="relative mx-auto max-w-5xl px-4 sm:px-8 py-14 lg:py-16">
          <p className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] text-primary"><Radio size={14} /> Live with Jason</p>
          <h1 className="mt-3 font-serif text-4xl font-extrabold tracking-tight sm:text-5xl">Live Q&A sessions</h1>
          <p className="mt-4 max-w-2xl text-lg text-muted-foreground">Register for an upcoming session, ask your questions live, and I'll email you the join link and a reminder.</p>
        </div>
      </section>

      <div className="mx-auto max-w-5xl px-4 sm:px-8 py-12">
        {isAdmin && <AdminForm onCreated={load} />}

        <h2 className="font-serif text-2xl font-bold">Upcoming sessions</h2>
        {loading ? (
          <p className="mt-4 text-sm text-muted-foreground">Loading…</p>
        ) : data.upcoming.length === 0 ? (
          <p className="mt-4 text-sm text-muted-foreground" data-testid="live-empty">No sessions scheduled right now — check back soon!</p>
        ) : (
          <div className="mt-5 space-y-4" data-testid="live-upcoming">
            {data.upcoming.map((s) => (
              <div key={s.id} data-testid={`live-session-${s.id}`} className="rounded-2xl border border-border bg-card p-6">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="font-serif text-xl font-semibold">{s.title}</p>
                    <p className="mt-1 flex items-center gap-2 text-sm text-muted-foreground"><CalendarDays size={14} className="text-primary" /> {fmtWhen(s.scheduled_at)} · {s.platform}</p>
                    {s.description && <p className="mt-2 text-sm text-muted-foreground">{s.description}</p>}
                  </div>
                  <div className="flex items-center gap-2">
                    {isAdmin && <span className="flex items-center gap-1 text-xs text-muted-foreground"><Users size={13} /> {s.registrations}</span>}
                    <Button onClick={() => setOpenId(openId === s.id ? null : s.id)} data-testid={`live-rsvp-${s.id}`} className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                      {openId === s.id ? "Close" : "RSVP / Remind me"}
                    </Button>
                    {isAdmin && <button onClick={() => del(s.id)} data-testid={`live-delete-${s.id}`} className="flex h-9 w-9 items-center justify-center rounded-full border border-border text-muted-foreground hover:border-red-500 hover:text-red-500"><Trash2 size={15} /></button>}
                  </div>
                </div>
                {openId === s.id && <RegisterForm sessionId={s.id} onDone={() => { setOpenId(null); load(); }} />}
              </div>
            ))}
          </div>
        )}

        {data.past.length > 0 && (
          <>
            <h2 className="mt-12 font-serif text-2xl font-bold">Past sessions</h2>
            <div className="mt-5 space-y-3" data-testid="live-past">
              {data.past.map((s) => (
                <div key={s.id} className="flex items-center gap-3 rounded-xl border border-border bg-background px-4 py-3">
                  <Check size={16} className="text-primary" />
                  <span className="flex-1 text-sm">{s.title}</span>
                  <span className="font-mono text-xs text-muted-foreground">{fmtWhen(s.scheduled_at)}</span>
                  {isAdmin && <button onClick={() => del(s.id)} data-testid={`live-delete-past-${s.id}`} className="flex h-8 w-8 items-center justify-center rounded-full border border-border text-muted-foreground hover:border-red-500 hover:text-red-500"><Trash2 size={14} /></button>}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
